import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { useAtom } from 'jotai';
import {
  Controller,
  type RegisterOptions,
  useFormContext
} from 'react-hook-form';

import { isCustomerCloudSourcePageAtom } from '@/pages/Administration/Devices/Devices.store';
import { organizationsAtom } from '@/pages/Organizations';

import useCloudSourceForm from '../../CloudSourceHooks/useCloudSourceForm.hooks';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import {
  limaDevicesAtom,
  selectedSourceDetailsAtom
} from '../../SourceCreate.store';
import { SourceDialogMode } from '../../SourceCreate.types';
import { validateIPAddress, validationRules } from '../../SourceCreate.utils';

/**
 * Renders organization name, Lima device selector, and IP address fields.
 *
 * Responsibilities:
 * - Display organization name (from selected org or looked up in edit mode)
 * - Lima device dropdown (New mode) or read-only device name (Edit mode)
 * - IP address input field with validation
 * - Conditional rendering based on customer cloud source page context
 */
export const OrgDeviceIPFields = () => {
  const { register, control, formState } = useFormContext();
  const { errors } = formState;

  const [limaDevices] = useAtom(limaDevicesAtom);
  const [isCustomerCloudSourcePage] = useAtom(isCustomerCloudSourcePageAtom);
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const [organizations] = useAtom(organizationsAtom);

  const { mode, selectedOrgData } = useCloudSourceForm();

  // When "All Organizations" is selected (id=0) and editing a source,
  // fetch the organization name from organizations atom using organization_id from selectedSourceDetails
  const getOrganizationName = () => {
    if (selectedOrgData.id > 0) {
      return selectedOrgData.name;
    }

    // In edit mode with "All Organizations" selected
    if (
      mode === SourceDialogMode.Edit &&
      selectedSourceDetails?.organization_id
    ) {
      const org = organizations.find(
        (o) => o.id === selectedSourceDetails.organization_id
      );
      return org?.name || '';
    }

    return '';
  };

  const renderLimaDeviceField = () => (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <InputLabel>
        Device Name <span> *</span>
      </InputLabel>
      <FormControl fullWidth>
        {mode === SourceDialogMode.New ? (
          <Controller
            name="device_uid"
            control={control}
            rules={validationRules.device_uid as RegisterOptions}
            defaultValue=""
            render={({ field }) => (
              <Select
                sx={{ width: '100%' }}
                {...field}
                error={!!formState.errors.device_uid}
                onChange={(e) => {
                  field.onChange(e);
                }}
                value={field.value}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Select Device</em>;
                  }
                  return (
                    limaDevices?.find((item) => item.device_uid === selected)
                      ?.host_name || ''
                  );
                }}
              >
                <MenuItem disabled value="">
                  <em>Select Device</em>
                </MenuItem>
                {limaDevices?.map((item) => (
                  <MenuItem key={item.device_uid} value={item.device_uid}>
                    {item.host_name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        ) : (
          <>
            <TextField
              {...register('device_uid')}
              name="device_uid"
              sx={{ display: 'none' }}
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              {...register('device_name')}
              name="device_name"
              slotProps={{ input: { readOnly: true } }}
            />
          </>
        )}
        {errors.device_uid && (
          <ErrorMessage message={errors.device_uid?.message} />
        )}
      </FormControl>
    </Grid>
  );

  return (
    <>
      {/* Organization name from the global organization dropdown */}
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <InputLabel htmlFor="device-name-input">
          Organization<span> *</span>
        </InputLabel>
        <FormControl fullWidth>
          <TextField
            {...register('organization_name')}
            name="organization_name"
            value={getOrganizationName()}
            disabled
          />
          <TextField
            type="hidden"
            {...register('org_id')}
            sx={{ display: 'none' }}
          />
        </FormControl>
      </Grid>

      {/* Device name from the devices table */}
      {!isCustomerCloudSourcePage && renderLimaDeviceField()}

      {isCustomerCloudSourcePage && (
        <div style={{ display: 'none' }}>
          <TextField type="text" {...register('device_uid')} />
        </div>
      )}

      {/* IP Address */}
      {!isCustomerCloudSourcePage && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputLabel>IP Address</InputLabel>
          <FormControl fullWidth>
            <TextField
              {...control.register('ip_address', {
                validate: validateIPAddress
              })}
              error={!!errors?.ip_address}
            />
            {errors?.ip_address && (
              <ErrorMessage message={errors.ip_address.message} />
            )}
          </FormControl>
        </Grid>
      )}
    </>
  );
};
